import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import CustomModal from "../../../components/common/customModal";
import CustomSelect from "../../../components/common/customSelect";
import CustomDatePicker from "../../../components/common/customDatePicker";
import dashboardService from "../../../services/DashboardServices";
import { IdeaCaptureType } from "../../../types/adminSection";
import dayjs, { Dayjs } from "dayjs";
import { showToast } from "../../../components/common/toastNotification";

interface IdeaCaptureConfigProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface Option {
  label: string;
  value: string;
}

const IdeaCaptureConfig: React.FC<IdeaCaptureConfigProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [divisionOptions, setDivisionOptions] = useState<Option[]>([]);
  const [functionOptions, setFunctionOptions] = useState<Option[]>([]);
  const [subFunctionOptions, setSubFunctionOptions] = useState<Option[]>([]);

  const [formData, setFormData] = useState<IdeaCaptureType>({
    division: "",
    functionName: "",
    subFunctionName: "",
    startDate: "",
    endDate: "",
  });

  const [formErrors, setFormErrors] = useState<IdeaCaptureType>({
    division: "",
    functionName: "",
    subFunctionName: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    if (open) {
      resetForm();
      fetchConfigurationData();
      fetchDivisions();
    }
  }, [open]);
  // get the configuration data
  const fetchConfigurationData = async () => {
    try {
      const { data } = await dashboardService.GetConfigurationData();
      if (data?.success && data.data) {
        const config = data.data;
        setFormData({
          division: config.division || "",
          functionName: config.functionName || "",
          subFunctionName: config.subFunctionName || "",
          startDate: config.startDate
            ? dayjs(config.startDate).toISOString()
            : "",
          endDate: config.endDate ? dayjs(config.endDate).toISOString() : "",
        });

        if (config.division) await fetchFunctions(config.division);
        if (config.functionName) await fetchSubFunctions(config.functionName);
      }
    } catch (error) {
      console.error("Failed to fetch configuration data", error);
    }
  };

  const resetForm = () => {
    setFormData({
      division: "",
      functionName: "",
      subFunctionName: "",
      startDate: "",
      endDate: "",
    });
    setFunctionOptions([]);
    setSubFunctionOptions([]);
  };
  const handleDropDownOnChange = (event: {
    target: { name: string; value: string | number };
  }) => {
    const { name, value } = event.target;

    if (name === "division") {
      fetchFunctions(value.toString());
      setFormData((prev) => ({
        ...prev,
        division: value.toString(),
        functionName: "",
        subFunctionName: "",
      }));
      setFunctionOptions([]);
      setSubFunctionOptions([]);
    } else if (name === "functionName") {
      fetchSubFunctions(value.toString());
      setFormData((prev) => ({
        ...prev,
        functionName: value.toString(),
        subFunctionName: "",
      }));
      setSubFunctionOptions([]);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleChange = (
    field: string,
    value: string | number | Dayjs | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
  };
  const fetchDivisions = async () => {
    try {
      const { data } = await dashboardService.getDivisionListAPI();
      if (data?.success) {
        setDivisionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.division,
            value: item.division,
          })) || []
        );
      }
    } catch (error) {
      console.error("Failed to fetch divisions", error);
    }
  };

  const fetchFunctions = async (division: string) => {
    try {
      const { data } = await dashboardService.GetFunctionListByDivisionIdAPI(
        division
      );
      if (data?.success) {
        setFunctionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.functionName,
            value: item.functionName,
          })) || []
        );
      }
    } catch (error) {
      console.error("Failed to fetch functions", error);
    }
  };

  const fetchSubFunctions = async (func: string) => {
    try {
      const { data } = await dashboardService.GetSubFunctionByFunctionAPI(func);
      if (data?.success) {
        setSubFunctionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.subFunctionName,
            value: item.subFunctionName,
          })) || []
        );
      }
    } catch (error) {
      console.error("Failed to fetch sub-functions", error);
    }
  };
  // save the data
  const handleSaveData = async () => {
    const errors: Partial<IdeaCaptureType> = {};

    if (!formData.startDate) errors.startDate = "Start date is required";
    if (!formData.endDate) errors.endDate = "End date is required";
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (start >= end) {
        showToast("Start date must be before End date", "error");
        return;
      }
    }
    if (!formData.division) errors.division = "Division is required";
    if (!formData.functionName) errors.functionName = "Function is required";
    if (!formData.subFunctionName)
      errors.subFunctionName = "Sub-function is required";

    setFormErrors(errors as IdeaCaptureType);

    if (Object.keys(errors).length > 0) return;

    try {
      const inputData = {
        division: formData.division,
        functionName: formData.functionName,
        subFunctionName: formData.subFunctionName,
        startDate: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : "",
        endDate: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : "",
        isActive: true,
      };

      const response = await dashboardService.REImagineConfiguration(inputData);
      if (response.data.success) {
        showToast(response.data.message, "success");
        onSave();
        onClose();
      } else {
        console.error("Failed to save idea capture config");
      }
    } catch (error) {
      console.error("Error saving idea capture config", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      onSave={handleSaveData}
      saveTitle="Update"
      title="Ideathon Configuration"
      width={800}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomDatePicker
            name="startDate"
            label="Start Date"
            placeholder="Select Start Date"
            value={formData.startDate ? dayjs(formData.startDate) : null}
            onChange={(value: Dayjs | null) => handleChange("startDate", value)}
            required
            shouldDisableDate={() => false}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CustomDatePicker
            name="endDate"
            label="End Date"
            placeholder="Select End Date"
            value={formData.endDate ? dayjs(formData.endDate) : null}
            onChange={(value: Dayjs | null) => handleChange("endDate", value)}
            required
            shouldDisableDate={() => false}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CustomSelect
            name="division"
            label="Division Name"
            placeHolder="Select Division Name"
            onChange={handleDropDownOnChange}
            options={divisionOptions}
            value={formData.division}
            error={formErrors.division}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CustomSelect
            name="functionName"
            label="Function Name"
            placeHolder="Select Function Name"
            onChange={handleDropDownOnChange}
            options={functionOptions}
            value={formData.functionName}
            error={formErrors.functionName}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CustomSelect
            name="subFunctionName"
            label="Sub Function Name"
            placeHolder="Select Sub Function Name"
            onChange={handleDropDownOnChange}
            options={subFunctionOptions}
            value={formData.subFunctionName}
            error={formErrors.subFunctionName}
            required
          />
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default IdeaCaptureConfig;
